﻿import { useEffect, useMemo, useState } from "react";
import { formatPercent } from "../../../utilities/format";
import i18n from "../../../i18n";
import { FormControlLabel, Paper, Skeleton, Switch, Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { DateTime } from "luxon";
import { useQuery } from '@tanstack/react-query';
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { faArrowsUpDownLeftRight, faChevronDown, faGripVertical, faChevronUp, faIndustry, faListCheck, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { PlantStatusCardEntryDTO, PlantStatusMachineDTO } from "../../../DTOs/Dashboards/PlantStatusCardDataDTO";
import { plantStatusQuery } from "./dashboardQueries";
import { useDashboardCard } from "../../Contexts/DashboardCardContext";
import useShiftTimes from "../../Hooks/useShiftTimes";
import useShiftLockInfo from "../../Hooks/useShiftLockInfo";
import { isCurrentShift } from "../../../utilities/helpers/shiftLockStateHelpers";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";
import { arrangeItems } from "../dashboardLayout";
import { useItemOrder } from "../useItemOrder";
import { Button } from "../../Buttons/Button";
import { TileMover } from "./TileMover";
import { useOnlineStatus } from "../../Hooks/useOnlineStatus";
import { operationsMachineTarget, stoppagesMachineTarget } from "../../../utilities/helpers/dashboardLinks";
import { LinkTarget } from "../../../utilities/helpers/linkTarget";
import { useAccessibleTarget } from "../../AccessLink/useAccessibleTarget";
import {
  ShiftWindow,
  formatMachinePercent,
  machineStatusColour,
  machineStatusTiming,
  sortPlantStatusMachines,
  availabilityBand,
  resolveShiftScope,
} from "./plantStatusMachines";

import { shiftNameToLetter } from "../../../types/Shift";
import { machineStatusDescription, machineStatusName } from "../../../i18n/vocabulary";

export const PLANT_STATUS_MIN_WIDTH = "min-w-[480px]";

export const PlantStatusCard: React.FC = () => {
  const { selectedDate, selectedShift, shiftStartTime, shiftEndTime } = useShiftTimes();
  const { shiftLockState } = useShiftLockInfo(selectedDate, selectedShift);
  const { isFullScreen, onScreen, reportLive, editing: customisingDashboard } = useDashboardCard();
  const [tileOrder, saveShownTileOrder] = useItemOrder("plant-status");
  const [arranging, setArranging] = useState(false);

  if (arranging && customisingDashboard) setArranging(false);
  const isOnline = useOnlineStatus();
  const queryEnabled = selectedDate.isValid && !!selectedShift;
  const [wantsFullDay, setWantsFullDay] = useState(false);
  const { fullDay, locked } = resolveShiftScope(
    selectedDate,
    selectedShift,
    wantsFullDay,
    shiftLockState?.currentShift ?? null
  );
  const { data: plantData = null, isLoading, dataUpdatedAt } = useQuery({
    ...plantStatusQuery(selectedDate, selectedShift, fullDay),
    enabled: queryEnabled,
    subscribed: onScreen,
  });

  const entries = useMemo(
    () => arrangeItems(plantData?.entries ?? [], tileOrder, entry => entry.plantType),
    [plantData, tileOrder]
  );

  const shift = useMemo<ShiftWindow>(
    () => ({ start: shiftStartTime, end: shiftEndTime }),
    [shiftStartTime, shiftEndTime]
  );

  const now = useMemo(
    () => (dataUpdatedAt > 0 ? DateTime.fromMillis(dataUpdatedAt) : DateTime.now()),
    [dataUpdatedAt]
  );

  const showSkeleton = isLoading || !queryEnabled;
  const isEmpty = !showSkeleton && !plantData?.entries?.length;
  const tileGapClass = isFullScreen ? "gap-x-2 gap-y-4 md:gap-x-4" : "gap-x-1 gap-y-2 md:gap-x-4 md:gap-y-2";

  useEffect(() => {
    const current = isCurrentShift(selectedDate, shiftNameToLetter(selectedShift), shiftLockState);

    reportLive({
      current,
      tooltipText: fullDay
        ? i18n.t("dashboard:live.wholeDay")
        : current
          ? i18n.t("dashboard:live.latest")
          : i18n.t("dashboard:live.selected"),
    });
  }, [fullDay, reportLive, selectedDate, selectedShift, shiftLockState]);

  return (
    <Paper className="overflow-hidden !shadow-none !border-none !w-full h-full text-center relative !rounded-none !bg-theme-surface-2 flex flex-col">
      <div className="flex items-center px-3 py-1 border-b border-theme-border">
        {entries.length > 1 && (arranging ? (
          <Button className="animate-fade-in" variant="primary" size="sm" onClick={() => setArranging(false)}>{i18n.t("dashboard:frame.done")}</Button>
        ) : (
          <Tooltip
            className="animate-fade-in"
            title={customisingDashboard ? i18n.t("dashboard:plant.finishCustomising") : ""}
            placement="right">
            <span>
              <Button
                variant="ghost"
                size="sm"
                className="font-medium !pl-0"
                leftIcon={faArrowsUpDownLeftRight}
                disabled={customisingDashboard}
                onClick={() => setArranging(true)}
              >
                {i18n.t("dashboard:plant.rearrange")}
              </Button>
            </span>
          </Tooltip>
        ))}
        <Tooltip
          title={locked
            ? i18n.t("dashboard:plant.wholeDayLocked")
            : i18n.t("dashboard:plant.wholeDayHint")}
          placement="left"
        >
          <span className="ml-auto">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={fullDay}
                  disabled={locked}
                  onChange={(_, checked) => setWantsFullDay(checked)}
                />
              }
              label={<span className="text-sm text-theme-text">{i18n.t("dashboard:plant.wholeDay")}</span>}
            />
          </span>
        </Tooltip>
      </div>

      {showSkeleton && (
        <div className="overflow-x-auto px-3">
          <div className={`flex flex-wrap animate-fade-in justify-center ${PLANT_STATUS_MIN_WIDTH} ${tileGapClass}`}>
            {[...Array(16)].map((_, i) => (
              <div
                key={i}
                className={`animate-pulse rounded-xl flex-1 min-w-[160px] max-w-[280px] shadow-md px-2 py-4 flex flex-col items-center justify-center bg-theme-table-heading ${isFullScreen ? "h-[250px] md:min-w-[300px] md:px-4 md:py-6" : "h-[170px]"}`}
              >
                <Skeleton animation="wave" variant="text" width="60%" height={28} className="bg-theme-table-row-even" />
                <Skeleton animation="wave" variant="text" width="40%" height={20} className="bg-theme-table-row-even" />
                <Skeleton animation="wave" variant="text" width="40%" height={20} className="bg-theme-table-row-even" />
                <Skeleton animation="wave" variant="text" width="50%" height={20} className="bg-theme-table-row-even" />
              </div>
            ))}
          </div>
        </div>
      )}

      {isEmpty && (
        <DashboardCardEmptyState
          isFullScreen={isFullScreen}
          icon={faIndustry}
          title={isOnline ? i18n.t("dashboard:plant.emptyTitle") : i18n.t("table.offlineTitle")}
          subtitle={isOnline
            ? i18n.t("dashboard:plant.empty")
            : i18n.t("dashboard:plant.offline")}
        />
      )}
      {!isEmpty && (
        <div className={`overflow-x-auto flex-1 transition-all p-3 ${!showSkeleton ? "opacity-100 fade-up" : "opacity-0"}`}>
          <div className={`${PLANT_STATUS_MIN_WIDTH}`}>
            <div className={`h-full flex flex-wrap justify-center ${tileGapClass}`}>
              {arranging ? (
                <TileMover keys={entries.map(entry => entry.plantType)} onMove={saveShownTileOrder}>
                  {entries.map(plant => (
                    <MovableTile key={plant.plantType} plant={plant} isFullScreen={isFullScreen} />
                  ))}
                </TileMover>
              ) : entries.map(plant => (
                <PlantStatusTile key={plant.plantType} plant={plant} isFullScreen={isFullScreen} shift={shift} now={now} fullDay={fullDay} />
              ))}
            </div>
          </div>
        </div>
      )}
    </Paper>
  );
};

const OperatingDelayLabel: React.FC = () => (
  <Tooltip arrow title={i18n.t("dashboard:plant.operatingDelayFormula")} placement="top">
    <span className="cursor-help">{i18n.t("dashboard:plant.operatingDelay")}</span>
  </Tooltip>
);

const AVAILABILITY_CLASSES = {
  success: { tile: "bg-theme-success", tooltip: "border-theme-success !shadow-theme-success" },
  warning: { tile: "bg-theme-warning", tooltip: "border-theme-warning !shadow-theme-warning" },
  danger: { tile: "bg-theme-danger", tooltip: "border-theme-danger !shadow-theme-danger" },
};

const availabilityOf = (plant: PlantStatusCardEntryDTO) =>
  AVAILABILITY_CLASSES[availabilityBand(plant.availability, plant.availabilityTargetPercentage)];

const tileClassName = (plant: PlantStatusCardEntryDTO, isFullScreen?: boolean) => `
    animate-fade-in rounded-xl flex-1 min-w-[160px] max-w-[280px] text-white px-2 py-4 flex flex-col items-center justify-center transition
    shadow-[0_0_4px_var(--theme-shadow)] hover:shadow-[0_0_8px_var(--theme-shadow)] border border-theme-table-heading hover:border-theme-table-heading-contrast
    hover:translate-y-[-2px] h-[180px]
    ${isFullScreen ? "md:h-[250px] md:min-w-[300px] md:px-4 md:py-6" : "md:h-[200px]"}
    ${availabilityOf(plant).tile}
    `;

const TileBody = ({ plant, isFullScreen }: { plant: PlantStatusCardEntryDTO; isFullScreen?: boolean }) => (
  <>
    <Typography variant="h6" className={`!font-bold text-white ${isFullScreen ? "md:!text-3xl md:!mb-2" : ""}`}>
      {plant.plantType}
    </Typography>
    <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
      {i18n.t("dashboard:plant.vehicles", { vehicles: plant.vehicles })}
    </Typography>
    <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
      {i18n.t("dashboard:plant.uoa", { value: formatPercent(plant.uofA * 100, 2) })}
    </Typography>
    <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
      {i18n.t("dashboard:plant.availability", { value: formatPercent(plant.availability * 100, 2) })}
    </Typography>
    <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
      {i18n.t("dashboard:plant.assetUtil", { value: formatPercent(plant.assetUtilization * 100, 2) })}
    </Typography>
    <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
      <OperatingDelayLabel />: {formatMachinePercent(plant.operatingDelayPercent)}
    </Typography>
  </>
);

const MovableTile = ({ plant, isFullScreen }: { plant: PlantStatusCardEntryDTO; isFullScreen?: boolean }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({ id: plant.plantType });
  const { setNodeRef: setDropNodeRef, isOver } = useDroppable({ id: plant.plantType });

  return (
    <div
      ref={node => {
        setNodeRef(node);
        setDropNodeRef(node);
      }}
      {...listeners}
      className={`${tileClassName(plant, isFullScreen)} relative cursor-grab ${isDragging ? "opacity-40" : ""} ${isOver && !isDragging ? "ring-2 ring-theme-focus" : ""}`}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        aria-label={i18n.t("dashboard:frame.move", { title: plant.plantType })}
        className="absolute top-1 left-1 px-2 py-1 cursor-grab text-lg text-white/80 hover:text-white"
      >
        <FontAwesomeIcon icon={faGripVertical} />
      </button>
      <TileBody plant={plant} isFullScreen={isFullScreen} />
    </div>
  );
};

export const PlantStatusTile: React.FC<{
  plant: PlantStatusCardEntryDTO;
  isFullScreen?: boolean;
  shift: ShiftWindow;
  now: DateTime;
  fullDay: boolean;
}> = ({ plant, isFullScreen, shift, now, fullDay }) => {
  const machines = useMemo(() => sortPlantStatusMachines(plant.machines ?? []), [plant.machines]);
  const [isOpenBreakdown, setIsOpenBreakdown] = useState(false);
  const tileClasses = tileClassName(plant, isFullScreen);
  const body = <TileBody plant={plant} isFullScreen={isFullScreen} />;

  if (machines.length === 0) return <div className={tileClasses}>{body}</div>;

  return (
    <Tooltip
      title={<MachineBreakdown plantType={plant.plantType} machines={machines} shift={shift} now={now} fullDay={fullDay} />}
      placement="bottom"
      onOpen={() => setIsOpenBreakdown(true)}
      onClose={() => setIsOpenBreakdown(false)}
      enterTouchDelay={0}
      leaveTouchDelay={20_000}
      leaveDelay={50}
      slotProps={{
        tooltip: {
          className: `!bg-theme-surface !text-theme-text !p-0 !m-0 !max-w-none border-2 !shadow !rounded-md overflow-hidden ${availabilityOf(plant).tooltip}`,
        },
      }}
    >
      <div className={`${tileClasses} relative`}>
        {body}
        <FontAwesomeIcon
          icon={isOpenBreakdown ? faChevronUp : faChevronDown}
          className={`absolute bottom-2 right-2 text-sm opacity-90 ${isFullScreen ? "md:bottom-4 md:right-4 md:text-xl" : ""}`}
        />
      </div>
    </Tooltip>
  );
};

export const MachineBreakdown: React.FC<{
  plantType: string;
  machines: PlantStatusMachineDTO[];
  shift: ShiftWindow;
  now: DateTime;
  fullDay?: boolean;
}> = ({ plantType, machines, shift, now, fullDay }) => (
  <div className="w-[min(88vw,26rem)] max-h-[20rem] text-left flex flex-col">
    <div className="sticky top-0 flex items-baseline justify-between gap-2 px-4 py-3 bg-theme-surface-2 border-b border-theme-border">
      <span className="font-bold text-theme-text text-lg">{plantType}</span>
      <span className="text-theme-text-muted text-xs">
        {i18n.t("dashboard:plant.machinesScope", {
          machines: i18n.t("dashboard:plant.machines", { count: machines.length }),
          scope: fullDay ? i18n.t("dashboard:plant.wholeDayScope") : i18n.t("dashboard:plant.thisShift"),
        })}
      </span>
    </div>

    <div className="flex flex-col flex-1 overflow-y-auto">
      {machines.map(machine => (
        <MachineRow key={machine.plantId} machine={machine} shift={shift} now={now} />
      ))}
    </div>
  </div>
);

const MachineRow: React.FC<{ machine: PlantStatusMachineDTO; shift: ShiftWindow; now: DateTime }> = ({ machine, shift, now }) => {
  const timing = machineStatusTiming(machine, shift, now);

  return (
    <div className="flex items-center gap-2 px-5 py-3 border-b border-theme-border last:border-b-0">
      <span
        className="w-3.5 h-3.5 rounded-full shrink-0 border-2 border-theme-table-heading-border"
        style={{ backgroundColor: machineStatusColour(machine) }}
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-bold text-theme-text text-sm">{machine.plantRef}</span>
          <span className="truncate text-theme-text-muted text-xs" title={machineStatusDescription(machine.status, machine.statusDetail) ?? machineStatusName(machine.status)}>
            {machineStatusName(machine.status)}{timing ? ` ${timing}` : ""}
          </span>
        </div>
        <div className="text-theme-text-muted text-xs truncate">
          {i18n.t("dashboard:plant.avail")} {formatMachinePercent(machine.availabilityPercentage)}
          {" · "}
          {i18n.t("dashboard:plant.uoaShort")} {formatMachinePercent(machine.utilizationOfAvailability)}
          {" · "}
          <OperatingDelayLabel /> {formatMachinePercent(machine.operatingDelayPercent)}
          {machine.location ? ` · ${machine.location}` : ""}
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <MachineChip
          target={operationsMachineTarget(machine.plantRef, machine.plantId)}
          icon={faListCheck}
          label={i18n.t("dashboard:plant.openInOperations")}
        />
        <MachineChip
          target={stoppagesMachineTarget(machine.plantRef)}
          icon={faTriangleExclamation}
          label={i18n.t("dashboard:plant.openInStoppages")}
        />
      </div>
    </div>
  );
};

const MachineChip: React.FC<{
  target: LinkTarget | null;
  icon: IconDefinition;
  label: string;
}> = ({ target, icon, label }) => {
  const accessible = useAccessibleTarget(target);

  if (!accessible) return null;

  return (
    <Link
      to={accessible.to}
      title={label}
      aria-label={label}
      className="flex items-center justify-center w-7 h-7 rounded-lg border border-theme-border bg-theme-surface-2 text-theme-text transition hover:border-theme-focus hover:bg-theme-surface"
    >
      <FontAwesomeIcon icon={icon} className="text-xs" />
    </Link>
  );
};
